import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  AdModel,
  AdsFilteringType,
  AdsGetAllResponseType,
  AdsSortingType,
  NotificationTypeEnum,
  PaginationType,
} from '@/types';
import { MinimizedLocation } from '@/types/location';
import { adsProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { createLatestRequestGuard } from '@/components/LocationAutocomplete/latestRequestGuard';

import {
  getAdsApiFilter,
  getAdsPageWithinBounds,
  getAdsPageWithinPageCount,
  getAdsPagination,
  getAdsSorting,
  parseAdsSearchParams,
  stringifyAdsSearchParams,
} from './utils/adsSearchParams';
import type { TopAgentFilterValue } from './utils/adsSearchParams';
import { createAmbassadorSeed } from './utils/createAmbassadorSeed';

interface UseAdsStorefrontDataProps {
  initialAmbassadorSeed?: string;
  changeQueryParams: (params: string) => void;
  defaultSortingField: string;
  enableAmbassadorPromotion: boolean;
  enableTopAgentFilter: boolean;
  isRent: boolean;
  queryParamsStr: string;
  rowsPerPage: number;
  setMinimizedLocations: Dispatch<SetStateAction<MinimizedLocation[]>>;
}

interface PaginationTotalContext {
  key: string;
  total: number;
}

const getPaginationTotalContextKey = (
  filteringObj: AdsFilteringType,
  sortingObj: AdsSortingType,
  topAgent: TopAgentFilterValue
) => JSON.stringify({ filteringObj, sortingObj, topAgent });

export const useAdsStorefrontData = ({
  changeQueryParams,
  defaultSortingField,
  enableAmbassadorPromotion,
  enableTopAgentFilter,
  initialAmbassadorSeed,
  isRent,
  queryParamsStr,
  rowsPerPage,
  setMinimizedLocations,
}: UseAdsStorefrontDataProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<string | null>(null);
  const [ads, setAds] = useState<AdModel[]>();
  const [filteringObj, setFilteringObj] = useState<AdsFilteringType>();
  const [sortingObj, setSortingObj] = useState<AdsSortingType>();
  const [paginationObj, setPaginationObj] = useState<PaginationType>();
  const [pageCount, setPageCount] = useState<number>();
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [ambassadorSeed] = useState(() =>
    enableAmbassadorPromotion ? initialAmbassadorSeed || createAmbassadorSeed() : undefined
  );
  const hasSuccessfulLoadRef = useRef(false);
  const paginationTotalContextRef = useRef<PaginationTotalContext | null>(null);
  const changeQueryParamsRef = useRef(changeQueryParams);
  const requestGuardRef = useRef(createLatestRequestGuard());
  const requestGuard = requestGuardRef.current;

  changeQueryParamsRef.current = changeQueryParams;

  const loadAds = useCallback(async () => {
    const queryParams = parseAdsSearchParams(queryParamsStr);

    if (!enableTopAgentFilter && queryParams.topAgent) {
      changeQueryParamsRef.current(
        stringifyAdsSearchParams({
          ...queryParams,
          topAgent: undefined,
        })
      );
      return;
    }

    const topAgent = enableTopAgentFilter ? queryParams.topAgent : undefined;
    const hasCreatorQuery = Boolean(
      queryParams['creator.id'] || queryParams['creator.kind'] || queryParams['creator.name']
    );

    if (topAgent && hasCreatorQuery) {
      changeQueryParamsRef.current(
        stringifyAdsSearchParams({
          ...queryParams,
          'creator.id': undefined,
          'creator.kind': undefined,
          'creator.name': undefined,
        })
      );
      return;
    }

    const parsedFilteringObj = getAdsApiFilter(queryParams, isRent);
    const newFilteringObj = topAgent
      ? { ...parsedFilteringObj, creator: undefined }
      : parsedFilteringObj;
    const newSortingObj = getAdsSorting(queryParams, defaultSortingField);
    const newPaginationObj = getAdsPagination(queryParams, rowsPerPage);
    const effectiveSeed = topAgent ? undefined : ambassadorSeed;
    const paginationTotalContextKey = getPaginationTotalContextKey(
      newFilteringObj,
      newSortingObj,
      topAgent
    );
    const paginationTotalContext = paginationTotalContextRef.current;
    const isSamePaginationTotalContext = paginationTotalContext?.key === paginationTotalContextKey;
    const savedPaginationTotal = isSamePaginationTotalContext ? paginationTotalContext.total : 0;
    const requestId = requestGuard.start();
    let shouldFinalizeRequest = true;

    setIsLoading(true);
    if (!hasSuccessfulLoadRef.current) {
      setFilteringObj({ ...newFilteringObj });
      setSortingObj({ ...newSortingObj });
      setPaginationObj({
        ...newPaginationObj,
        total: savedPaginationTotal,
      });
    } else if (!isSamePaginationTotalContext) {
      setPaginationObj(undefined);
      setPageCount(undefined);
    }

    try {
      const topAgentFilter =
        topAgent === 'all'
          ? { ambassadorOnly: true }
          : topAgent
            ? { ambassadorUserId: topAgent }
            : undefined;
      const request =
        effectiveSeed || topAgentFilter
          ? adsProvider.getAds(
              false,
              newFilteringObj,
              newSortingObj,
              newPaginationObj,
              effectiveSeed,
              topAgentFilter
            )
          : adsProvider.getAds(false, newFilteringObj, newSortingObj, newPaginationObj);
      const { ok, data, status, message } = await request;

      if (!requestGuard.isLatest(requestId)) return;

      if (ok) {
        const {
          items,
          total,
          locations,
          pageCount: responsePageCount,
        } = data as AdsGetAllResponseType;
        const authoritativePageCount =
          effectiveSeed && Number.isInteger(responsePageCount) && (responsePageCount as number) >= 0
            ? responsePageCount
            : undefined;
        const minimumTotalForCurrentPage = newPaginationObj.offset + items.length;
        const totalForPagination =
          authoritativePageCount !== undefined
            ? total
            : items.length
              ? Math.max(
                  total,
                  minimumTotalForCurrentPage,
                  total < minimumTotalForCurrentPage ? savedPaginationTotal : 0
                )
              : total;
        paginationTotalContextRef.current = {
          key: paginationTotalContextKey,
          total: totalForPagination,
        };
        const boundedPage =
          authoritativePageCount !== undefined
            ? getAdsPageWithinPageCount(newPaginationObj.page, authoritativePageCount)
            : getAdsPageWithinBounds(
                newPaginationObj.page,
                totalForPagination,
                newPaginationObj.limit
              );

        if (boundedPage !== newPaginationObj.page) {
          shouldFinalizeRequest = false;
          changeQueryParamsRef.current(
            stringifyAdsSearchParams({
              ...queryParams,
              page: boundedPage,
              topAgent: enableTopAgentFilter ? queryParams.topAgent : undefined,
            })
          );
          return;
        }

        const requestedLocationIds = Array.isArray(newFilteringObj.locationId)
          ? newFilteringObj.locationId
          : newFilteringObj.locationId
            ? [newFilteringObj.locationId]
            : [];

        setMinimizedLocations(locations);
        setSelectedMarker((currentMarker) =>
          currentMarker &&
          requestedLocationIds.includes(currentMarker) &&
          locations.some(({ locationId }) => locationId === currentMarker)
            ? currentMarker
            : null
        );
        setAds(items);
        setFilteringObj({ ...newFilteringObj });
        setSortingObj({ ...newSortingObj });
        setPageCount(authoritativePageCount);
        setPaginationObj({
          ...newPaginationObj,
          total: totalForPagination,
        });
        hasSuccessfulLoadRef.current = true;
        setInitialLoadError(null);
      } else {
        if (!hasSuccessfulLoadRef.current) {
          setInitialLoadError(message || 'Failed to load ads');
        }
        dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
      }
    } catch (error) {
      if (!requestGuard.isLatest(requestId)) return;

      const message = error instanceof Error ? error.message : 'Failed to load ads';
      if (!hasSuccessfulLoadRef.current) {
        setInitialLoadError(message);
      }
      dispatch(appSetNotification(NotificationTypeEnum.Error, message));
    } finally {
      if (requestGuard.isLatest(requestId) && shouldFinalizeRequest) {
        setIsLoading(false);
        setHasCompletedInitialLoad(true);
      }
    }
  }, [
    ambassadorSeed,
    defaultSortingField,
    dispatch,
    enableTopAgentFilter,
    isRent,
    queryParamsStr,
    requestGuard,
    rowsPerPage,
    setMinimizedLocations,
  ]);

  useEffect(() => {
    void loadAds();

    return () => requestGuard.invalidate();
  }, [loadAds, requestGuard]);

  const retry = useCallback(() => {
    if (isLoading) return;
    void loadAds();
  }, [isLoading, loadAds]);

  return {
    ads,
    filteringObj,
    hasCompletedInitialLoad,
    initialLoadError,
    isLoading,
    paginationObj,
    pageCount,
    retry,
    selectedMarker,
    setFilteringObj,
    setSelectedMarker,
    sortingObj,
  };
};
